import React from 'react'

const getSeatGuid = (seat, row) =>
  seat.seat_guid || seat.uuid || `seat-${row.row_number}-${seat.seat_number}`

function getRowLabels(row) {
  const seats = row?.seats || []
  if (!seats.length || row.show_row_numbers === false) return []

  const text = row.row_label !== undefined && row.row_label !== ''
    ? String(row.row_label)
    : String(row.row_number || '')
  if (!text) return []

  const curveAngle = Math.abs(Number(row.curve_angle || 0))
  const isFullCircle = curveAngle >= 359.5 || Boolean(row.is_circular)
  const position = row.row_label_position || 'both'

  if (isFullCircle) {
    const first = seats[0]
    const center = seats.reduce((sum, seat) => ({
      x: sum.x + (seat.position?.x ?? 0) / seats.length,
      y: sum.y + (seat.position?.y ?? 0) / seats.length,
    }), { x: 0, y: 0 })
    const dx = (first.position?.x ?? 0) - center.x
    const dy = (first.position?.y ?? 0) - center.y
    const distance = Math.hypot(dx, dy) || 1
    const radius = first.radius || row.seat_radius || 10
    return [{
      text,
      x: (first.position?.x ?? 0) + (dx / distance) * (radius + 14),
      y: (first.position?.y ?? 0) + (dy / distance) * (radius + 14),
    }]
  }

  const result = []
  const first = seats[0]
  const last = seats[seats.length - 1]
  if (position === 'both' || position === 'left') {
    const next = seats[1]
    let dx = -1
    let dy = 0
    if (next) {
      dx = (first.position?.x ?? 0) - (next.position?.x ?? 0)
      dy = (first.position?.y ?? 0) - (next.position?.y ?? 0)
      const distance = Math.hypot(dx, dy) || 1
      dx /= distance
      dy /= distance
    }
    const radius = first.radius || row.seat_radius || 10
    result.push({ text, x: (first.position?.x ?? 0) + dx * (radius + 14), y: (first.position?.y ?? 0) + dy * (radius + 14) })
  }
  if (seats.length > 1 && (position === 'both' || position === 'right')) {
    const previous = seats[seats.length - 2]
    let dx = (last.position?.x ?? 0) - (previous.position?.x ?? 0)
    let dy = (last.position?.y ?? 0) - (previous.position?.y ?? 0)
    const distance = Math.hypot(dx, dy) || 1
    dx /= distance
    dy /= distance
    const radius = last.radius || row.seat_radius || 10
    result.push({ text, x: (last.position?.x ?? 0) + dx * (radius + 14), y: (last.position?.y ?? 0) + dy * (radius + 14) })
  }
  return result
}

function Area({ area, index }) {
  const positioned = Boolean(area.position)
  const x = positioned ? area.position.x || 0 : area.x || 0
  const y = positioned ? area.position.y || 0 : area.y || 0
  const rotation = area.rotation || 0
  const shape = area.shape || (area.type === 'circle' ? 'circle' : 'rectangle')
  const width = area.rectangle?.width || area.width || area.size?.width || 120
  const height = area.rectangle?.height || area.height || area.size?.height || 60
  const fill = area.color || '#CCCCCC'
  const stroke = area.border_color || fill
  const label = area.text?.text || area.label || area.name || ''
  const textColor = area.text?.color || '#333333'
  const fontSize = area.text?.fontSize || 16
  const transform = `translate(${x}, ${y}) rotate(${rotation})`

  return (
    <g key={area.uuid || area.id || `area-${index}`} transform={transform} opacity={area.opacity ?? 1}>
      {shape === 'circle' ? (
        <circle cx={positioned ? 0 : width / 2} cy={positioned ? 0 : height / 2} r={area.circle?.radius || width / 2} fill={fill} stroke={stroke} strokeWidth={1.5} />
      ) : shape === 'ellipse' ? (
        <ellipse cx={0} cy={0} rx={area.ellipse?.radius?.x || width / 2} ry={area.ellipse?.radius?.y || height / 2} fill={fill} stroke={stroke} strokeWidth={1.5} />
      ) : shape === 'polygon' && area.polygon?.points ? (
        <polygon points={area.polygon.points.map(point => `${point.x},${point.y}`).join(' ')} fill={fill} stroke={stroke} strokeWidth={1.5} />
      ) : shape === 'text' ? null : (
        <rect
          x={positioned ? -width / 2 : 0}
          y={positioned ? -height / 2 : 0}
          width={width}
          height={height}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.5}
          rx={area.type === 'stage' || area.is_stage ? 10 : 4}
        />
      )}
      {label && (
        <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fill={textColor} fontSize={`${fontSize}px`} fontWeight="700" style={{ pointerEvents: 'none' }}>
          {label}
        </text>
      )}
    </g>
  )
}

export default function SeatMapCanvas({
  planJson,
  seatStatuses = {},
  selectedGuids = [],
  ticketTiers = [],
  zoom = 1,
  pan = { x: 0, y: 0 },
  onSeatClick,
  onSeatHover,
  onSeatLeave,
  disabled = false,
  allowReservedSelection = false,
  allowUnavailableClick = false,
  adminReservationMode = false,
  isDraft = false,
  backgroundFill = '#F8FAFC',
  boundaryStroke = '#E2E8F0',
}) {
  const width = planJson?.size?.width || 900
  const height = planJson?.size?.height || 700
  const selectedSet = new Set(selectedGuids)
  const categoryColors = new Map()
  ticketTiers.forEach(tier => {
    if (tier?.name && tier?.color) categoryColors.set(tier.name, tier.color)
  })
  ;(planJson?.categories || []).forEach(category => {
    if (category?.name && category?.color && !categoryColors.has(category.name)) categoryColors.set(category.name, category.color)
  })

  return (
    <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
      <rect x={0} y={0} width={width} height={height} fill={backgroundFill} stroke={boundaryStroke} strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
      {planJson?.background_image_url && (
        <image
          href={planJson.background_image_url}
          x={planJson.background_settings?.x || 0}
          y={planJson.background_settings?.y || 0}
          width={planJson.background_settings?.width || width}
          height={planJson.background_settings?.height || height}
          opacity={planJson.background_settings?.opacity ?? 0.3}
          style={{ pointerEvents: 'none' }}
        />
      )}
      {(planJson?.areas || []).map((area, index) => <Area key={`plan-area-${index}`} area={area} index={index} />)}
      {(planJson?.zones || []).map((zone, zoneIndex) => (
        <g key={zone.uuid || zone.name || zoneIndex} transform={`translate(${zone.position?.x || 0}, ${zone.position?.y || 0})`}>
          {(zone.areas || []).map((area, index) => <Area key={`zone-${zoneIndex}-area-${index}`} area={area} index={index} />)}
          {(zone.rows || []).map((row, rowIndex) => {
            const rowLabel = row.row_label || String(row.row_number || rowIndex + 1)
            return (
              <g key={row.uuid || row.row_number || rowIndex} transform={`translate(${row.position?.x || 0}, ${row.position?.y || 0}) rotate(${row.rotation || 0})`}>
                {getRowLabels(row).map((label, index) => (
                  <text key={`row-label-${index}`} x={label.x} y={label.y} transform={row.rotation ? `rotate(${-row.rotation}, ${label.x}, ${label.y})` : undefined} textAnchor="middle" dominantBaseline="central" fill="#94A3B8" fontSize="11px" fontWeight="700" style={{ pointerEvents: 'none' }}>
                    {label.text}
                  </text>
                ))}
                {(row.seats || []).map((seat, seatIndex) => {
                  const guid = getSeatGuid(seat, row)
                  const selected = selectedSet.has(guid)
                  const status = isDraft ? 'available' : seatStatuses[guid]?.status || 'available'
                  const booked = status === 'booked' || status === 'sold'
                  const unavailable = status === 'unavailable' || status === 'reserved' || status === 'draft_reserved'
                  const held = status === 'held' || status === 'hold'
                  const color = categoryColors.get(seat.category) || '#0756C9'
                  const unavailableForPublic = booked || unavailable || (held && !selected)
                  const selectable = !disabled && (allowUnavailableClick || (allowReservedSelection
                    ? (!booked && !held && (status === 'available' || unavailable))
                    : !unavailableForPublic))
                  const fill = adminReservationMode
                    ? booked
                      ? '#E2E8F0'
                      : unavailable
                        ? '#94A3B8'
                        : held
                          ? '#CBD5E1'
                          : color
                    : selected ? color : unavailable ? '#64748B' : booked ? '#1E293B' : held ? '#475569' : color
                  const opacity = adminReservationMode
                    ? 1
                    : !selected && unavailable ? 0.35 : !selected && booked ? 0.35 : !selected && held ? 0.5 : 1
                  const seatText = adminReservationMode
                    ? booked ? '' : unavailable ? 'B' : held ? 'H' : selected ? '✓' : seat.seat_number
                    : selected ? '✓' : seat.seat_number
                  const seatRotation = (row.rotation || 0) + (seat.rotation || 0)
                  const zoneName = zone.name && zone.name !== 'Default Zone' ? zone.name : ''
                  return (
                    <g
                      key={guid || seatIndex}
                      data-seat="true"
                      transform={`translate(${seat.position?.x || 0}, ${seat.position?.y || 0}) rotate(${seat.rotation || 0})`}
                      style={{ cursor: selectable ? 'pointer' : 'not-allowed' }}
                      onClick={event => {
                        event.stopPropagation()
                        if (selectable) onSeatClick?.(event, zone, row, seat)
                      }}
                      onMouseEnter={event => onSeatHover?.(event, zone, row, seat, { guid, status, color })}
                      onMouseLeave={event => onSeatLeave?.(event, zone, row, seat)}
                      role="button"
                      tabIndex={selectable ? 0 : -1}
                      aria-label={`${zoneName ? `${zoneName}, ` : ''}row ${rowLabel}, seat ${seat.seat_number}${selected ? ', selected' : ''}`}
                      aria-pressed={selected}
                      onKeyDown={event => {
                        if ((event.key === 'Enter' || event.key === ' ') && selectable) {
                          event.preventDefault()
                          onSeatClick?.(event, zone, row, seat)
                        }
                      }}
                    >
                      {selected && <circle r={14} fill="none" stroke={color} strokeWidth={2.5} opacity={0.85} />}
                      <circle r={10} fill={fill} opacity={opacity} stroke={selected ? '#FFFFFF' : 'rgba(0,0,0,0.3)'} strokeWidth={selected ? 2.5 : 1} />
                      <g transform={seatRotation ? `rotate(${-seatRotation})` : undefined}>
                        <text y={0} textAnchor="middle" dominantBaseline="central" fill={adminReservationMode && (booked || unavailable || held) ? '#334155' : '#FFFFFF'} fontSize={adminReservationMode && (booked || unavailable || held) ? '9px' : '8.5px'} fontWeight="700" style={{ pointerEvents: 'none' }}>
                          {seatText}
                        </text>
                      </g>
                    </g>
                  )
                })}
              </g>
            )
          })}
        </g>
      ))}
    </g>
  )
}
